/**
 * Tow accessorial builder
 */

import { AccessorialType } from '../../constants/accessorial-types.js'
import type { Accessorial } from '../../types/accessorial.js'
import type { BuildTowOptions } from './types.js'
import { calcDelayPrice, calcDelayPay } from '../shared/calculations.js'
import { buildAccessorial } from '../shared/buildAccessorial.js'

/**
 * Build Tow Accessorial
 *
 * Used when a vehicle needs to be towed.
 *
 * Cost Responsibility Breakdown:
 * - HopDrive: Pays actual tow cost
 * - Customer: Pays tow cost + delay time if responsible
 * - Driver: Paid for idle time during tow
 *
 * @param options - Tow accessorial options
 * @returns Insertable tow accessorial object
 * @throws Error if validation fails
 *
 * @example
 * ```typescript
 * const towAccessorial = buildTowAccessorial({
 *   moveId: 12345,
 *   customerResponsible: true,
 *   towCost: 150.00,
 *   driverIdleTimeMinutes: 60,
 *   customerPricePerMinute: 0.33,
 *   driverPayPerMinute: 0.17
 * })
 * // Result: { cost: 150.00, ar_amount: 169.80, ap_amount: 10.20, ... }
 * ```
 */
export function buildTowAccessorial(options: BuildTowOptions): Accessorial {
  // Validation for required props
  if (!options || typeof options !== 'object') {
    throw new Error('Failed to build tow accessorial: Invalid options prop!')
  }
  if (!options.moveId || (typeof options.moveId !== 'number' && typeof options.moveId !== 'bigint')) {
    throw new Error('Failed to build tow accessorial: Invalid moveId prop!')
  }
  if (typeof options.customerResponsible !== 'boolean') {
    throw new Error('Failed to build tow accessorial: Invalid customerResponsible prop!')
  }
  if (typeof options.towCost !== 'number') {
    throw new Error('Failed to build tow accessorial: Invalid towCost prop!')
  }

  // Validation for optional props
  if (options.driverIdleTimeMinutes !== undefined && typeof options.driverIdleTimeMinutes !== 'number') {
    throw new Error('Failed to build tow accessorial: Invalid driverIdleTimeMinutes prop!')
  }
  if (options.customerPricePerMinute !== undefined && typeof options.customerPricePerMinute !== 'number') {
    throw new Error('Failed to build tow accessorial: Invalid customerPricePerMinute prop!')
  }
  if (options.driverPayPerMinute !== undefined && typeof options.driverPayPerMinute !== 'number') {
    throw new Error('Failed to build tow accessorial: Invalid driverPayPerMinute prop!')
  }
  if (options.notes !== undefined && options.notes !== null && typeof options.notes !== 'string') {
    throw new Error('Failed to build tow accessorial: Invalid notes prop!')
  }

  // Calculate tow & delay price to customer (if customer is responsible)
  const towPrice = options.customerResponsible ? options.towCost : 0
  const delayPrice = options.customerResponsible
    ? calcDelayPrice(options.driverIdleTimeMinutes || 0, options.customerPricePerMinute)
    : 0

  // Calculate delay pay to driver
  const delayPay = calcDelayPay(options.driverIdleTimeMinutes || 0, options.driverPayPerMinute)

  // Build accessorial using base builder
  return buildAccessorial({
    moveId: options.moveId,
    code: AccessorialType.TOW.code,
    cost: options.towCost,
    ar_amount: towPrice + delayPrice,
    ap_amount: delayPay,
    notes: options.notes || AccessorialType.TOW.desc,
  })
}
